'use client';

import HTMLReactParser from 'html-react-parser';
import Link from 'next/link';
import { type FC, useState } from 'react';

import styles from 'components/SearchDropdown/searchDropdown.module.scss';

import type { Result } from 'components/Search/Listing/utils/retrieveSearch';

interface SearchDropdownProps {
  results: Result[];
  loading: boolean;
  inputValue: string;
  isOpen?: boolean;
  maxResults?: number;
  variant?: 'nav' | 'hero';
}

const SearchDropdown: FC<SearchDropdownProps> = ({
  results,
  loading,
  inputValue,
  isOpen = true,
  maxResults = 5,
  variant = 'hero',
}) => {
  const [page, setPage] = useState(1);
  const pageSize = maxResults;
  const start = (page - 1) * pageSize;
  const postsToShow = results.slice(start, start + pageSize);
  const hasResults = results.length > 0;

  if (!isOpen || !inputValue || inputValue.length < 2) {
    return null;
  }

  return (
    <div className={`${styles.dropdown} ${variant === 'nav' ? styles.dropdownNav : styles.dropdownHero}`}>
      {loading && <p className={styles.status}>Searching...</p>}
      {!loading && !hasResults && <p className={styles.status}>No results found.</p>}
      {!loading && hasResults && (
        <ul className={styles.resultsList}>
          {postsToShow.map((result, index) => {
            const title = result.title ? HTMLReactParser(result.title) : '';
            const rawBody = result.body || '';
            let body = rawBody;
            if (!rawBody.includes('<strong>')) {
              const suffix = rawBody.length > 120 ? '...' : '';
              body = `${rawBody.slice(0, 120)}${suffix}`;
            }
            const parsedBody = body ? HTMLReactParser(body) : '';

            return (
              <li key={`${result.builderId}-${index}`} className={styles.resultItem}>
                {result.slug ? (
                  <Link href={result.slug} className={styles.resultLink}>
                    {title && <span className={styles.resultTitle}>{title}</span>}
                    {parsedBody && <span className={styles.resultBody}>{parsedBody}</span>}
                  </Link>
                ) : (
                  <div className={styles.resultLink} aria-disabled="true">
                    {title && <span className={styles.resultTitle}>{title}</span>}
                    {parsedBody && <span className={styles.resultBody}>{parsedBody}</span>}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {!loading && hasResults && results.length > pageSize && (
        <div className={styles.pagination}>
          <button
            type="button"
            className={styles.pageButton}
            disabled={page === 1}
            onClick={() => setPage(p => p - 1)}
            aria-label="Previous page"
          >
            &larr;
          </button>
          <span className={styles.pageInfo}>
            {page} / {Math.ceil(results.length / pageSize)}
          </span>
          <button
            type="button"
            className={styles.pageButton}
            disabled={page >= Math.ceil(results.length / pageSize)}
            onClick={() => setPage(p => p + 1)}
            aria-label="Next page"
          >
            &rarr;
          </button>
        </div>
      )}
    </div>
  );
};

export default SearchDropdown;
